import React, { useEffect, useState, useCallback } from "react";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadFile, ACCEPT_UPLOAD, MAX_UPLOAD_BYTES } from "@/lib/files";
import { Paperclip, Plus, Trash2, ExternalLink, Upload, Download, FileText, Loader2 } from "lucide-react";
import { SectionCard, inputCls } from "./ui";

// Per-phase supporting docs: URL links (stored in phase data) + uploaded file assets (object storage, internal only).
export default function SupportingMaterials({ phaseKey }) {
  const { phaseData, setPhase, project } = useProject();
  const phase = phaseData[phaseKey] || {};
  const materials = phase.materials || [];
  const phaseNum = parseInt(phaseKey.replace("phase", ""), 10) || 1;
  const projectId = project?.id;

  const setMaterials = (m) => setPhase(phaseKey, { ...phase, materials: m });
  const add = () => setMaterials([...materials, { id: uid(), label: "", url: "" }]);
  const upd = (i, k, v) => { const m = [...materials]; m[i] = { ...m[i], [k]: v }; setMaterials(m); };
  const del = (i) => setMaterials(materials.filter((_, idx) => idx !== i));

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadFiles = useCallback(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/files`).then((r) => setFiles(r.data.filter((f) => f.phase === phaseNum))).catch(() => {});
  }, [projectId, phaseNum]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) return toast.error("File exceeds the 10MB limit");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("phase", String(phaseNum));
      fd.append("label", file.name);
      fd.append("file", file);
      await api.post(`/projects/${projectId}/files`, fd);
      toast.success("File uploaded");
      loadFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (id) => {
    try { await api.delete(`/projects/${projectId}/files/${id}`); toast.success("File removed"); loadFiles(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <SectionCard
      title="Supporting Materials"
      desc="Link documents or upload completed worksheets & files (internal — not shown in client reports). Links appear in the exported reports."
      right={
        <div className="flex items-center gap-2">
          <label data-testid={`upload-file-btn-${phaseKey}`} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload File
            <input type="file" accept={ACCEPT_UPLOAD} onChange={onUpload} disabled={uploading || !projectId} className="hidden" data-testid={`file-input-${phaseKey}`} />
          </label>
          <button data-testid={`add-material-${phaseKey}`} onClick={add} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Link</button>
        </div>
      }
      testid={`materials-${phaseKey}`}
    >
      <div className="space-y-2">
        {materials.length === 0 && files.length === 0 && (
          <p className="text-sm text-slate-400 flex items-center gap-2"><Paperclip className="h-4 w-4" /> No supporting materials yet. Add a link or upload a file.</p>
        )}

        {files.map((f) => (
          <div key={f.id} data-testid={`file-row-${phaseKey}-${f.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm text-slate-700 truncate">{f.label}</span>
              <span className="text-xs text-slate-400 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={() => downloadFile(`/projects/${projectId}/files/${f.id}/download`, f.original_filename)} className="text-slate-500 hover:text-emerald-600" title="Download"><Download className="h-4 w-4" /></button>
              <button data-testid={`delete-file-${phaseKey}-${f.id}`} onClick={() => removeFile(f.id)} className="text-slate-300 hover:text-red-500" title="Remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}

        {materials.map((m, i) => (
          <div key={m.id || i} className="grid sm:grid-cols-[1fr_1.6fr_auto_auto] gap-2 items-center" data-testid={`material-row-${phaseKey}-${i}`}>
            <input value={m.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label (e.g. Survey Results)" className={inputCls} data-testid={`material-label-${phaseKey}-${i}`} />
            <input value={m.url} onChange={(e) => upd(i, "url", e.target.value)} placeholder="https://…" className={inputCls} data-testid={`material-url-${phaseKey}-${i}`} />
            {m.url ? (
              <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 justify-self-center" title="Open link"><ExternalLink className="h-4 w-4" /></a>
            ) : <span className="w-4" />}
            <button onClick={() => del(i)} className="text-slate-300 hover:text-red-500 justify-self-center"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
